if (!customElements.get('media-gallery')) {
  class ProductZoomDialog {
    constructor(gallery, openingLink) {
      this.gallery = gallery;
      this.openingLink = openingLink;
      this.items = this.getItems();
      this.index = Math.max(0, this.items.findIndex((item) => item.link === openingLink));
      this.pointers = new Map();
      this.lastTap = null;
      this.previousFocus = openingLink;

      if (this.items.length === 0) return;

      this.build();
      this.show(this.index);
      this.closeBtn.focus();
    }

    getItems() {
      return Array.from(this.gallery.querySelectorAll('.js-zoom-link'))
        .filter((link) => {
          const mediaItem = link.closest('.media-viewer__item');
          return mediaItem && mediaItem.getClientRects().length > 0;
        })
        .map((link) => {
          const mediaItem = link.closest('.media-viewer__item');
          const mediaId = mediaItem.dataset.mediaId;
          const image = link.querySelector('.product-image');
          const thumb = this.gallery.querySelector(
            `.media-thumbs__item[data-media-id="${mediaId}"] img`
          );

          return {
            link,
            desktop: {
              src: link.dataset.zoomSrc || link.href,
              width: Number(link.dataset.zoomWidth),
              height: Number(link.dataset.zoomHeight)
            },
            mobile: {
              src: link.dataset.zoomMobileSrc || link.dataset.zoomSrc || link.href,
              width: Number(link.dataset.zoomMobileWidth),
              height: Number(link.dataset.zoomMobileHeight)
            },
            thumbSrc: thumb ? thumb.currentSrc : image.currentSrc,
            alt: image.alt || ''
          };
        });
    }

    build() {
      const labels = this.openingLink.dataset;
      this.root = document.createElement('div');
      this.root.className = 'product-zoom-dialog';
      this.root.setAttribute('role', 'dialog');
      this.root.setAttribute('aria-modal', 'true');
      this.root.setAttribute('aria-label', labels.zoomDialog || '');
      this.root.tabIndex = -1;
      this.root.innerHTML = `
        <div class="product-zoom-dialog__stage">
          <div class="product-zoom-dialog__image-wrap">
            <img class="product-zoom-dialog__image" draggable="false">
          </div>
          <div class="product-zoom-dialog__loader loading-spinner loading-spinner--out" role="status"></div>
        </div>
        <div class="product-zoom-dialog__ui">
          <button type="button" class="product-zoom-dialog__button product-zoom-dialog__close">
            <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
              <path d="M7 7l18 18M25 7L7 25"></path>
            </svg>
          </button>
          <button type="button" class="product-zoom-dialog__button product-zoom-dialog__arrow product-zoom-dialog__arrow--previous">
            <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
              <path d="M27 16H5m8-8-8 8 8 8"></path>
            </svg>
          </button>
          <button type="button" class="product-zoom-dialog__button product-zoom-dialog__arrow product-zoom-dialog__arrow--next">
            <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
              <path d="M5 16h22m-8-8 8 8-8 8"></path>
            </svg>
          </button>
        </div>
        <div class="product-zoom-dialog__preview">
          <div class="product-zoom-dialog__thumbs"></div>
          <div class="product-zoom-dialog__pagination" aria-hidden="true"></div>
        </div>
      `;

      this.stage = this.root.querySelector('.product-zoom-dialog__stage');
      this.imageWrap = this.root.querySelector('.product-zoom-dialog__image-wrap');
      this.image = this.root.querySelector('.product-zoom-dialog__image');
      this.loader = this.root.querySelector('.product-zoom-dialog__loader');
      this.ui = this.root.querySelector('.product-zoom-dialog__ui');
      this.closeBtn = this.root.querySelector('.product-zoom-dialog__close');
      this.prevBtn = this.root.querySelector('.product-zoom-dialog__arrow--previous');
      this.nextBtn = this.root.querySelector('.product-zoom-dialog__arrow--next');
      this.thumbs = this.root.querySelector('.product-zoom-dialog__thumbs');
      this.pagination = this.root.querySelector('.product-zoom-dialog__pagination');

      this.closeBtn.setAttribute('aria-label', labels.zoomClose || '');
      this.prevBtn.setAttribute('aria-label', labels.zoomPrevious || '');
      this.nextBtn.setAttribute('aria-label', labels.zoomNext || '');
      this.closeBtn.title = labels.zoomClose || '';
      this.prevBtn.title = labels.zoomPrevious || '';
      this.nextBtn.title = labels.zoomNext || '';

      this.buildPreview();

      if (this.items.length < 2) {
        this.root.classList.add('product-zoom-dialog--single');
      }

      this.closeHandler = this.close.bind(this);
      this.previousHandler = () => this.show(this.index - 1);
      this.nextHandler = () => this.show(this.index + 1);
      this.keyHandler = this.handleKeydown.bind(this);
      this.resizeHandler = this.handleResize.bind(this);
      this.pointerDownHandler = this.handlePointerDown.bind(this);
      this.pointerMoveHandler = this.handlePointerMove.bind(this);
      this.pointerUpHandler = this.handlePointerUp.bind(this);

      this.closeBtn.addEventListener('click', this.closeHandler);
      this.prevBtn.addEventListener('click', this.previousHandler);
      this.nextBtn.addEventListener('click', this.nextHandler);
      this.stage.addEventListener('pointerdown', this.pointerDownHandler);
      this.stage.addEventListener('pointermove', this.pointerMoveHandler);
      this.stage.addEventListener('pointerup', this.pointerUpHandler);
      this.stage.addEventListener('pointercancel', this.pointerUpHandler);
      this.root.addEventListener('keydown', this.keyHandler);
      window.addEventListener('resize', this.resizeHandler);
      window.addEventListener('on:breakpoint-change', this.resizeHandler);

      document.documentElement.classList.add('product-zoom-open');
      document.body.classList.add('product-zoom-open');
      document.body.appendChild(this.root);
    }

    buildPreview() {
      this.items.forEach((item, index) => {
        const button = document.createElement('button');
        const image = document.createElement('img');
        button.type = 'button';
        button.className = 'product-zoom-dialog__thumb';
        button.setAttribute('aria-label', `${index + 1}`);
        image.src = item.thumbSrc;
        image.alt = '';
        image.loading = index < 4 ? 'eager' : 'lazy';
        button.appendChild(image);
        button.addEventListener('click', () => this.show(index));
        this.thumbs.appendChild(button);
      });

      const pageCount = Math.ceil(this.items.length / 3);
      for (let index = 0; index < pageCount; index += 1) {
        const page = document.createElement('span');
        page.className = 'product-zoom-dialog__page';
        this.pagination.appendChild(page);
      }
    }

    isMobile() {
      return !theme.mediaMatches.md;
    }

    show(index) {
      this.index = (index + this.items.length) % this.items.length;
      const item = this.items[this.index];
      this.mobileMode = this.isMobile();
      const imageVariant = this.mobileMode ? item.mobile : item.desktop;
      this.loadToken = (this.loadToken || 0) + 1;
      const loadToken = this.loadToken;

      this.setControlsHidden(false);
      this.loader.classList.remove('loading-spinner--out');
      this.image.classList.remove('is-loaded');
      this.image.alt = item.alt;
      this.naturalWidth = imageVariant.width;
      this.naturalHeight = imageVariant.height;
      this.image.style.width = `${this.naturalWidth}px`;
      this.image.style.height = `${this.naturalHeight}px`;

      this.image.onload = () => {
        if (loadToken !== this.loadToken) return;
        this.naturalWidth = this.image.naturalWidth || this.naturalWidth;
        this.naturalHeight = this.image.naturalHeight || this.naturalHeight;
        this.image.style.width = `${this.naturalWidth}px`;
        this.image.style.height = `${this.naturalHeight}px`;
        this.loader.classList.add('loading-spinner--out');
        this.image.classList.add('is-loaded');
        this.fitImage(false);
      };
      this.image.src = imageVariant.src;

      this.updatePreview();
      this.fitImage(false);
      this.preloadAdjacent();
    }

    updatePreview() {
      const thumbButtons = Array.from(this.thumbs.children);
      thumbButtons.forEach((button, index) => {
        const active = index === this.index;
        button.classList.toggle('is-active', active);
        if (active) {
          button.setAttribute('aria-current', 'true');
          button.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        } else {
          button.removeAttribute('aria-current');
        }
      });

      const activePage = Math.floor(this.index / 3);
      Array.from(this.pagination.children).forEach((page, index) => {
        page.classList.toggle('is-active', index === activePage);
      });
    }

    preloadAdjacent() {
      if (this.items.length < 2) return;
      [this.index - 1, this.index + 1].forEach((index) => {
        const item = this.items[(index + this.items.length) % this.items.length];
        const imageVariant = this.isMobile() ? item.mobile : item.desktop;
        const image = new Image();
        image.src = imageVariant.src;
      });
    }

    getBounds() {
      const styles = getComputedStyle(this.root);
      const top = Number.parseFloat(styles.getPropertyValue('--product-zoom-top-inset'));
      const previewHeight = Number.parseFloat(
        styles.getPropertyValue('--product-zoom-preview-height')
      );
      const bottomInset = this.items.length > 1 ? previewHeight : 0;
      return {
        top,
        bottomEdge: window.innerHeight - bottomInset,
        width: window.innerWidth,
        height: window.innerHeight - top - bottomInset
      };
    }

    fitImage(animate = true) {
      if (!this.naturalWidth || !this.naturalHeight) return;
      const bounds = this.getBounds();
      this.fitScale = Math.min(
        bounds.width / this.naturalWidth,
        bounds.height / this.naturalHeight,
        1
      );
      this.scale = this.fitScale;
      this.x = (bounds.width - (this.naturalWidth * this.scale)) / 2;
      this.y = bounds.top + ((bounds.height - (this.naturalHeight * this.scale)) / 2);
      this.root.classList.toggle('product-zoom-dialog--zoom-allowed', this.fitScale < 0.99);
      this.root.classList.remove('product-zoom-dialog--zoomed');
      this.applyTransform(animate);
    }

    applyTransform(animate = false) {
      this.root.classList.toggle('product-zoom-dialog--animating', animate);
      this.imageWrap.style.transform = `translate3d(${this.x}px, ${this.y}px, 0) scale(${this.scale})`;
      if (animate) {
        const finishAnimation = () => {
          this.imageWrap.removeEventListener('transitionend', finishAnimation);
          this.imageWrap.removeEventListener('transitioncancel', finishAnimation);
          if (this.root) this.root.classList.remove('product-zoom-dialog--animating');
        };
        this.imageWrap.addEventListener('transitionend', finishAnimation);
        this.imageWrap.addEventListener('transitioncancel', finishAnimation);
      }
    }

    isZoomed() {
      return this.scale > this.fitScale + 0.01;
    }

    clampPosition() {
      const bounds = this.getBounds();
      const scaledWidth = this.naturalWidth * this.scale;
      const scaledHeight = this.naturalHeight * this.scale;

      if (scaledWidth <= bounds.width) {
        this.x = (bounds.width - scaledWidth) / 2;
      } else {
        this.x = Math.min(0, Math.max(bounds.width - scaledWidth, this.x));
      }

      if (scaledHeight <= bounds.height) {
        this.y = bounds.top + ((bounds.height - scaledHeight) / 2);
      } else {
        this.y = Math.min(bounds.top, Math.max(bounds.bottomEdge - scaledHeight, this.y));
      }
    }

    toggleZoom(clientX, clientY) {
      if (this.fitScale >= 0.99) return;
      if (this.isZoomed()) {
        this.fitImage();
        return;
      }

      const sourceX = (clientX - this.x) / this.scale;
      const sourceY = (clientY - this.y) / this.scale;
      this.scale = 1;
      this.x = clientX - (sourceX * this.scale);
      this.y = clientY - (sourceY * this.scale);
      this.clampPosition();
      this.root.classList.add('product-zoom-dialog--zoomed');
      this.applyTransform(true);
    }

    handlePointerDown(evt) {
      if (evt.button !== undefined && evt.button !== 0) return;
      evt.preventDefault();
      this.stage.setPointerCapture(evt.pointerId);
      this.pointers.set(evt.pointerId, { x: evt.clientX, y: evt.clientY });

      if (this.pointers.size === 2) {
        this.startPinch();
        return;
      }

      this.startDrag({ x: evt.clientX, y: evt.clientY });
    }

    startDrag(pointer) {
      this.gesture = {
        type: 'drag',
        startX: pointer.x,
        startY: pointer.y,
        imageX: this.x,
        imageY: this.y,
        zoomed: this.isZoomed()
      };
    }

    startPinch() {
      const [first, second] = Array.from(this.pointers.values());
      const midpoint = {
        x: (first.x + second.x) / 2,
        y: (first.y + second.y) / 2
      };
      this.gesture = {
        type: 'pinch',
        distance: Math.hypot(second.x - first.x, second.y - first.y),
        scale: this.scale,
        sourceX: (midpoint.x - this.x) / this.scale,
        sourceY: (midpoint.y - this.y) / this.scale
      };
    }

    handlePointerMove(evt) {
      if (!this.pointers.has(evt.pointerId)) return;
      evt.preventDefault();
      this.pointers.set(evt.pointerId, { x: evt.clientX, y: evt.clientY });

      if (this.pointers.size > 1) {
        if (!this.gesture || this.gesture.type !== 'pinch') this.startPinch();
        const [first, second] = Array.from(this.pointers.values());
        const midpoint = {
          x: (first.x + second.x) / 2,
          y: (first.y + second.y) / 2
        };
        const distance = Math.hypot(second.x - first.x, second.y - first.y);
        this.scale = Math.min(1, Math.max(
          this.fitScale * 0.8,
          this.gesture.scale * (distance / this.gesture.distance)
        ));
        this.x = midpoint.x - (this.gesture.sourceX * this.scale);
        this.y = midpoint.y - (this.gesture.sourceY * this.scale);
        this.root.classList.toggle('product-zoom-dialog--zoomed', this.isZoomed());
        this.applyTransform();
        return;
      }

      if (!this.gesture || this.gesture.type !== 'drag') return;
      const dx = evt.clientX - this.gesture.startX;
      const dy = evt.clientY - this.gesture.startY;
      this.x = this.gesture.imageX + dx;
      this.y = this.gesture.zoomed ? this.gesture.imageY + dy : this.gesture.imageY;
      this.applyTransform();
    }

    handlePointerUp(evt) {
      if (!this.pointers.has(evt.pointerId)) return;
      evt.preventDefault();
      const gesture = this.gesture;
      this.pointers.delete(evt.pointerId);

      if (!gesture) return;
      if (gesture.type === 'pinch') {
        if (this.scale < this.fitScale * 1.05) {
          this.fitImage();
        } else {
          this.clampPosition();
          this.root.classList.toggle('product-zoom-dialog--zoomed', this.isZoomed());
          this.applyTransform(true);
        }

        if (this.pointers.size === 1) {
          const remainingPointer = Array.from(this.pointers.values())[0];
          this.startDrag(remainingPointer);
        } else {
          this.gesture = null;
        }
        return;
      }

      const dx = evt.clientX - gesture.startX;
      const dy = evt.clientY - gesture.startY;
      const moved = Math.hypot(dx, dy) > 8;

      if (!gesture.zoomed && Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
        this.show(dx < 0 ? this.index + 1 : this.index - 1);
      } else if (!moved) {
        this.handleTap(evt);
      } else if (gesture.zoomed) {
        this.clampPosition();
        this.applyTransform(true);
      } else {
        this.fitImage(true);
      }
    }

    handleTap(evt) {
      if (evt.pointerType === 'mouse') {
        this.toggleZoom(evt.clientX, evt.clientY);
        return;
      }

      const now = Date.now();
      const isDoubleTap = this.lastTap
        && now - this.lastTap.time < 300
        && Math.hypot(evt.clientX - this.lastTap.x, evt.clientY - this.lastTap.y) < 30;

      if (isDoubleTap) {
        window.clearTimeout(this.singleTapTimer);
        this.lastTap = null;
        this.toggleZoom(evt.clientX, evt.clientY);
      } else {
        this.lastTap = { time: now, x: evt.clientX, y: evt.clientY };
        window.clearTimeout(this.singleTapTimer);
        this.singleTapTimer = window.setTimeout(() => {
          if (this.root) {
            this.setControlsHidden(
              !this.root.classList.contains('product-zoom-dialog--controls-hidden')
            );
          }
          this.lastTap = null;
        }, 300);
      }
    }

    setControlsHidden(hidden) {
      this.root.classList.toggle('product-zoom-dialog--controls-hidden', hidden);
      this.ui.toggleAttribute('inert', hidden);
      this.ui.setAttribute('aria-hidden', hidden ? 'true' : 'false');

      if (hidden && this.ui.contains(document.activeElement)) {
        const focusTarget = this.items.length > 1
          ? this.thumbs.children[this.index]
          : this.root;
        focusTarget.focus({ preventScroll: true });
      }
    }

    handleKeydown(evt) {
      if (evt.key === 'Escape') {
        evt.preventDefault();
        this.close();
      } else if (evt.key === 'ArrowLeft') {
        evt.preventDefault();
        this.show(this.index - 1);
      } else if (evt.key === 'ArrowRight') {
        evt.preventDefault();
        this.show(this.index + 1);
      } else if (evt.key === 'Tab') {
        const controls = Array.from(this.root.querySelectorAll('button:not([hidden])'))
          .filter((control) => {
            const controlsAreHidden = this.root.classList.contains(
              'product-zoom-dialog--controls-hidden'
            );
            return control.getClientRects().length > 0
              && !(controlsAreHidden && control.closest('.product-zoom-dialog__ui'));
          });
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (controls.length === 0) {
          evt.preventDefault();
          this.root.focus();
        } else if (evt.shiftKey && document.activeElement === first) {
          evt.preventDefault();
          last.focus();
        } else if (!evt.shiftKey && document.activeElement === last) {
          evt.preventDefault();
          first.focus();
        }
      }
    }

    handleResize() {
      if (this.mobileMode !== this.isMobile()) {
        this.show(this.index);
      } else {
        this.fitImage(false);
      }
    }

    close() {
      if (!this.root) return;
      this.loadToken = (this.loadToken || 0) + 1;
      this.image.onload = null;
      window.clearTimeout(this.singleTapTimer);
      window.removeEventListener('resize', this.resizeHandler);
      window.removeEventListener('on:breakpoint-change', this.resizeHandler);
      this.root.remove();
      this.root = null;
      document.documentElement.classList.remove('product-zoom-open');
      document.body.classList.remove('product-zoom-open');
      if (this.previousFocus && document.contains(this.previousFocus)) {
        this.previousFocus.focus();
      }
    }
  }

  class MediaGallery extends HTMLElement {
    constructor() {
      super();

      if (Shopify.designMode) {
        setTimeout(() => this.init(), 200);
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('on:debounced-resize', this.resizeHandler);

      if (this.resizeInitHandler) {
        window.removeEventListener('on:debounced-resize', this.resizeInitHandler);
      }

      if (this.productZoom) this.productZoom.close();
    }

    init() {
      this.section = this.closest('.js-product');
      this.mediaGroupingEnabled = this.hasAttribute('data-media-grouping-enabled')
        && this.getMediaGroupData();
      this.stackedScroll = this.dataset.stackedScroll;
      this.stackedUnderline = this.dataset.stackedUnderline === 'true' && !this.mediaGroupingEnabled;
      this.isFeatured = this.dataset.isFeatured === 'true';
      this.viewer = this.querySelector('.media-viewer');
      this.thumbs = this.querySelector('.media-thumbs');
      this.thumbsItems = this.querySelectorAll('.media-thumbs__item');
      this.controls = this.querySelector('.media-ctrl');
      this.prevBtn = this.querySelector('.media-ctrl__btn[name="prev"]');
      this.nextBtn = this.querySelector('.media-ctrl__btn[name="next"]');
      this.counterCurrent = this.querySelector('.media-ctrl__current-item');
      this.counterTotal = this.querySelector('.media-ctrl__total-items');
      this.liveRegion = this.querySelector('.media-gallery__status');
      this.zoomLinks = this.querySelectorAll('.js-zoom-link');
      this.xrButton = this.querySelector('.media-xr-button');

      if (this.mediaGroupingEnabled) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        this.resizeInitHandler = this.resizeInitHandler || this.initGallery.bind(this);
        window.addEventListener('on:debounced-resize', this.resizeInitHandler);
        this.setVisibleItems();
        this.previousMediaItem = this.querySelector('.media-viewer__item.is-current-variant');
        setTimeout(() => this.customSetActiveMedia(this.previousMediaItem, this.stackedScroll === 'always'), 200);
      } else {
        this.initGallery();
      }

      if (this.zoomLinks.length > 0) {
        this.zoomLinks.forEach((el) => {
          el.addEventListener('click', this.openProductZoom.bind(this));
        });
      }

      this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));
    }

    /**
     * Handle a change in variant on the page.
     * @param {Event} evt - variant change event dispatched by variant-picker
     */
    onVariantChange(evt) {
      if (this.mediaGroupingEnabled) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (evt.detail.variant && evt.detail.variant.featured_media) {
        const variantMedia = this.viewer.querySelector(
          `[data-media-id="${evt.detail.variant.featured_media.id}"]`
        );
        this.customSetActiveMedia(variantMedia, true);
      }
    }

    /**
     * Gets the media group from currently selected variant options.
     * @returns {?object}
     */
    getMediaGroupFromOptionSelectors() {
      const optionSelectors = this.section.querySelectorAll('.option-selector');
      if (optionSelectors.length > this.getMediaGroupData().groupOptionIndex) {
        const selector = optionSelectors[this.getMediaGroupData().groupOptionIndex];
        if (selector.dataset.selectorType === 'dropdown') {
          return selector.querySelector('.custom-select__btn').textContent.trim();
        }
        return selector.querySelector('input:checked').value;
      }
      return null;
    }

    /**
     * Gets the variant media associations for a product.
     * @returns {?object}
     */
    getMediaGroupData() {
      if (typeof this.variantMediaData === 'undefined') {
        const dataEl = this.querySelector('.js-data-variant-media');
        this.variantMediaData = dataEl ? JSON.parse(dataEl.textContent) : false;
      }

      return this.variantMediaData;
    }

    /**
     * Gets an object mapping media to groups, and the reverse
     * @returns {?object}
     */
    getMediaGroupMap() {
      if (!this.mediaGroupMap) {
        this.mediaGroupMap = {
          groups: {}
        };

        // set up grouping
        const variantMediaData = this.getMediaGroupData();
        let currentMediaOptionName = false;
        this.viewerItems = this.querySelectorAll('.media-viewer__item');
        this.viewerItems.forEach((item) => {
          for (let i = 0; i < variantMediaData.variantMedia.length; i += 1) {
            if (parseInt(item.dataset.mediaId, 10) === variantMediaData.variantMedia[i].mediaId) {
              if (currentMediaOptionName !== variantMediaData.variantMedia[i].option) {
                currentMediaOptionName = variantMediaData.variantMedia[i].option;
              }
            }
          }
          if (currentMediaOptionName) {
            if (!this.mediaGroupMap.groups[currentMediaOptionName]) {
              this.mediaGroupMap.groups[currentMediaOptionName] = {
                name: currentMediaOptionName,
                items: []
              };
            }
            const groupItem = { main: item };
            if (this.thumbs) {
              groupItem.thumb = this.thumbs.querySelector(
                `[data-media-id="${item.dataset.mediaId}"].media-thumbs__item`
              );
            }
            this.mediaGroupMap.groups[currentMediaOptionName].items.push(groupItem);
          }
        });

        // add helper
        this.mediaGroupMap.groupFromItem = (item) => {
          const groups = Object.keys(this.mediaGroupMap.groups);
          for (let i = 0; i < groups; i += 1) {
            const group = groups[i];
            for (let j = 0; j < this.mediaGroupMap.groups[group].items.length; j += 1) {
              if (this.mediaGroupMap.groups[group].items[j] === item) {
                return this.mediaGroupMap.groups[group];
              }
            }
          }
          return this.mediaGroupMap.groups[Object.keys(this.mediaGroupMap.groups)[0]];
        };
      }

      return this.mediaGroupMap;
    }

    /**
     * Show only images associated to the current variant
     * @param {string} groupName - optional - Group to show (uses this.currentItem if empty)
     */
    setActiveMediaGroup(groupName) {
      const mediaGroupMap = this.getMediaGroupMap();
      const selectedGroup = groupName
        ? mediaGroupMap.groups[groupName]
        : mediaGroupMap.groupFromItem(this.currentItem);

      if (selectedGroup) {
        if (this.currentGroup !== selectedGroup) {
          this.currentGroup = selectedGroup;
          this.viewerItems.forEach((item) => {
            item.style.display = 'none';
            item.classList.remove('media-viewer__item--single');
          });
          this.thumbsItems.forEach((item) => {
            item.style.display = 'none';
          });

          let currentItemIsVisible = false;
          selectedGroup.items.forEach((item) => {
            item.main.style.display = '';
            if (item.thumb) {
              item.thumb.style.display = '';
            }
            if (item.main === this.currentItem) {
              currentItemIsVisible = true;
            }
          });
          this.setVisibleItems();

          // If current item is not in this group, set it as the active item
          if (!currentItemIsVisible) {
            this.customSetActiveMedia(selectedGroup.items[0].main, true);
          }

          // Handle single images on stacked view
          if (selectedGroup.items.length === 1) {
            selectedGroup.items[0].main.classList.add('media-viewer__item--single');
          }
        }
      } else {
        this.viewerItems.forEach((item) => {
          item.style.display = '';
        });
        this.thumbsItems.forEach((item) => {
          item.style.display = '';
        });
      }
    }

    /**
     * Initialises the media gallery slider and associated controls.
     */
    initGallery() {
      this.setVisibleItems();
      if (this.visibleItems.length <= 1) return;

      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
      this.currentIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);
      this.currentItem = this.visibleItems[this.currentIndex];
      this.addListeners();

      if (this.thumbs && this.currentItem) {
        this.currentThumb = this.thumbs.querySelector(
          `[data-media-id="${this.currentItem.dataset.mediaId}"]`
        );
      }

      if (!this.isFeatured && document.hasFocus()) {
        // Eager load the slider images for smooth UX
        this.viewer.querySelectorAll('.product-image[loading="lazy"]').forEach((img, index) => {
          setTimeout(() => {
            img.loading = 'eager';
          }, 500 * (index + 1));
        });
      }

      const currentItem = this.querySelector('.media-viewer__item.is-current-variant');
      this.customSetActiveMedia(currentItem, true);
    }

    addListeners() {
      this.viewer.addEventListener('scroll', this.handleScroll.bind(this));
      if (this.controls) this.controls.addEventListener('click', this.handleNavClick.bind(this));
      if (this.thumbs) this.thumbs.addEventListener('click', this.handleThumbClick.bind(this));
      this.resizeHandler = this.resizeHandler || this.handleResize.bind(this);
      window.addEventListener('on:debounced-resize', this.resizeHandler);
    }

    /**
     * Opens the Bergzeit-style full-screen product image gallery.
     * @param {MouseEvent} evt - Click event from a product image link.
     */
    openProductZoom(evt) {
      evt.preventDefault();
      if (this.productZoom) this.productZoom.close();
      this.productZoom = new ProductZoomDialog(this, evt.currentTarget);
    }

    /**
     * Handles 'scroll' events on the main media container.
     */
    handleScroll() {
      const newIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);

      if (newIndex !== this.currentIndex) {
        const viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

        // If scroll wasn't caused by a resize event, update the active media.
        if (viewerItemOffset === this.viewerItemOffset) {
          this.customSetActiveMedia(this.visibleItems[newIndex], false);
        }
      }
    }

    /**
     * Handles 'click' events on the controls container.
     * @param {object} evt - Event object.
     */
    handleNavClick(evt) {
      if (!evt.target.matches('.media-ctrl__btn')) return;

      const itemToShow = evt.target === this.nextBtn
        ? this.visibleItems[this.currentIndex + 1]
        : this.visibleItems[this.currentIndex - 1];

      this.viewer.scrollTo({ left: itemToShow.offsetLeft, behavior: 'smooth' });
    }

    /**
     * Handles 'click' events on the thumbnails container.
     * @param {object} evt - Event object.
     */
    handleThumbClick(evt) {
      const thumb = evt.target.closest('[data-media-id]');
      if (!thumb) return;

      const itemToShow = this.querySelector(`[data-media-id="${thumb.dataset.mediaId}"]`);
      this.customSetActiveMedia(itemToShow);

      MediaGallery.playActiveMedia(itemToShow);
    }

    /**
     * Handles debounced 'resize' events on the window.
     */
    handleResize() {
      // Reset distance from leading edge of one slide to the next.
      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

      if (this.thumbs && this.currentThumb) {
        this.checkThumbVisibilty(this.currentThumb);
      }
    }

    /**
     * Stub for variant-picker calls. Listening to change event instead.
     */
    // eslint-disable-next-line class-methods-use-this
    setActiveMedia() {}

    /**
     * Sets the active media item.
     * @param {Element} mediaItem - Media element to set as active.
     * @param {boolean} [scrollToItem=true] - Scroll to the active media item.
     */
    customSetActiveMedia(mediaItem, scrollToItem = true) {
      if (mediaItem === this.currentItem) return;
      window.pauseAllMedia(this);
      this.currentItem = mediaItem;
      this.currentIndex = this.visibleItems.indexOf(this.currentItem);

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        // Update the active class and scroll to the active media
        if (this.stackedUnderline) {
          if (this.previousMediaItem) this.previousMediaItem.classList.remove('is-active');
          mediaItem.classList.add('is-active');
          this.previousMediaItem = mediaItem;
        }

        if (this.stackedScroll !== 'never') {
          const y = mediaItem.getBoundingClientRect().top
            + document.documentElement.scrollTop - 150;

          // If the element is far enough away to scroll to it
          if (Math.abs(y - document.documentElement.scrollTop) > 300) {
            window.scrollTo({
              top: y < 100 ? 0 : y,
              behavior: 'smooth'
            });
          }
        }
        return;
      }

      if (scrollToItem) this.viewer.scrollTo({ left: this.currentItem.offsetLeft, behavior: 'smooth' });
      if (this.thumbs) this.setActiveThumb();

      if (this.controls) {
        if (this.prevBtn) {
          this.prevBtn.disabled = this.currentIndex === 0;
        }

        if (this.nextBtn) {
          this.nextBtn.disabled = this.currentIndex === this.visibleItems.length - 1;
        }

        if (this.counterCurrent) {
          this.counterCurrent.textContent = this.currentIndex + 1;
        }
      }

      this.announceLiveRegion(this.currentItem, this.currentIndex + 1);

      if (this.xrButton && mediaItem.dataset.mediaType === 'model') {
        this.xrButton.dataset.shopifyModel3dId = mediaItem.dataset.mediaId;
      }
    }

    /**
     * Sets the active thumbnail.
     */
    setActiveThumb() {
      this.currentThumb = this.thumbs.querySelector(
        `[data-media-id="${this.currentItem.dataset.mediaId}"]`
      );
      const btn = this.currentThumb.querySelector('button');

      this.thumbs.querySelectorAll('.media-thumbs__btn').forEach((el) => {
        el.classList.remove('is-active');
        el.removeAttribute('aria-current');
      });

      btn.classList.add('is-active');
      btn.setAttribute('aria-current', 'true');
      this.checkThumbVisibilty(this.currentThumb);
    }

    /**
     * Creates an array of the visible media items.
     */
    setVisibleItems() {
      this.viewerItems = this.querySelectorAll('.media-viewer__item');
      this.visibleItems = Array.from(this.viewerItems).filter((el) => el.clientWidth > 0);
      if (this.counterTotal) {
        this.counterTotal.textContent = this.visibleItems.length;
      }
    }

    /**
     * Ensures a thumbnail is in the visible area of the slider.
     * @param {Element} thumb - Thumb item element.
     */
    checkThumbVisibilty(thumb) {
      const scrollPos = this.thumbs.scrollLeft;
      const lastVisibleThumbOffset = this.thumbs.clientWidth + scrollPos;
      const thumbOffset = thumb.offsetLeft;

      if (thumbOffset + thumb.clientWidth > lastVisibleThumbOffset || thumbOffset < scrollPos) {
        this.thumbs.scrollTo({ left: thumbOffset, behavior: 'smooth' });
      }
    }

    /**
     * Updates the media gallery status.
     * @param {Element} mediaItem - Active media element.
     * @param {number} index - Active media index.
     */
    announceLiveRegion(mediaItem, index) {
      const image = mediaItem.querySelector('.media-viewer img');
      if (!image) return;

      this.liveRegion.setAttribute('aria-hidden', 'false');
      this.liveRegion.innerHTML = theme.strings.imageAvailable.replace('[index]', index);

      setTimeout(() => {
        this.liveRegion.setAttribute('aria-hidden', 'true');
      }, 2000);
    }

    /**
     * Loads the deferred media for the active item.
     * @param {Element} mediaItem - Active media element.
     */
    static playActiveMedia(mediaItem) {
      window.pauseAllMedia();
      const deferredMedia = mediaItem.querySelector('deferred-media');
      if (deferredMedia) deferredMedia.loadContent();
    }
  }

  customElements.define('media-gallery', MediaGallery);
}
